import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import ptBR from "date-fns/locale/pt-BR";

export const I18NEXT_STORAGE_KEY = "i18nextLng";

export const APP_LOCALES = {
  en: {
    dateFns: enUS,
    intl: "en",
    flagClass: "fi fi-us fis",
    currencyDisplay: "symbol",
    currencyPrefix: "",
  },
  es: {
    dateFns: es,
    intl: "es",
    flagClass: "fi fi-es fis",
    currencyDisplay: "code",
    currencyPrefix: "$",
  },
  pt: {
    dateFns: ptBR,
    intl: "pt-BR",
    flagClass: "fi fi-br fis",
    currencyDisplay: "code",
    currencyPrefix: "$",
  },
};

export const APP_SUPPORTED_LANGUAGES = Object.keys(APP_LOCALES);

export const DATE_FNS_LOCALES = APP_SUPPORTED_LANGUAGES.reduce(
  (locales, code) => {
    const locale = APP_LOCALES[code];
    locales[code] = locale.dateFns;
    locales[locale.intl] = locale.dateFns;
    return locales;
  },
  {}
);

export const normalizeAppLanguage = (language = "") => {
  if (!language || typeof language !== "string") {
    return null;
  }

  const shortLanguage = language.split("-")[0].toLowerCase();

  return APP_LOCALES[shortLanguage] ? shortLanguage : null;
};

export const getLanguageFormatted = (language) =>
  normalizeAppLanguage(language) || "en";

export const getAppLocale = (language) =>
  APP_LOCALES[getLanguageFormatted(language)];

export const getDateFnsLocale = (language) => getAppLocale(language).dateFns;

export const getIntlLocaleCode = (language) => getAppLocale(language).intl;

export const pickLocalizedValue = (language, values = {}) => {
  const code = getLanguageFormatted(language);
  if (Object.prototype.hasOwnProperty.call(values, code)) {
    return values[code];
  }
  return values.en;
};

export const getBrowserAppLanguage = () => {
  const browserLanguages =
    typeof navigator !== "undefined"
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language]
      : [];

  for (const browserLanguage of browserLanguages) {
    const normalizedLanguage = normalizeAppLanguage(browserLanguage);
    if (normalizedLanguage) {
      return normalizedLanguage;
    }
  }

  return "en";
};

export const getStoredAppLanguage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return normalizeAppLanguage(localStorage.getItem(I18NEXT_STORAGE_KEY));
};

export const persistAppLanguage = (language) => {
  const normalizedLanguage = normalizeAppLanguage(language);
  if (!normalizedLanguage || typeof window === "undefined") {
    return null;
  }

  localStorage.setItem(I18NEXT_STORAGE_KEY, normalizedLanguage);
  return normalizedLanguage;
};

export const resolveAppLanguage = ({
  userLanguage = null,
  storedLanguage = getStoredAppLanguage(),
  navigatorLanguage = getBrowserAppLanguage(),
  fallback = "en",
} = {}) => {
  return (
    normalizeAppLanguage(userLanguage) ||
    normalizeAppLanguage(storedLanguage) ||
    normalizeAppLanguage(navigatorLanguage) ||
    getLanguageFormatted(fallback)
  );
};
